import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { DialogService } from '../services/dialog.service';

export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

let bypassOnce = false;

/**
 * Lets a forced navigation (session expired, token invalid) skip the
 * "Discard unsaved changes?" prompt on its next deactivation check. The
 * user didn't choose to leave — there's no "keep editing" to offer them,
 * and stacking a confirm dialog on top of the "session expired" toast just
 * reads as a broken app. Self-resetting so it only ever suppresses the one
 * navigation it was raised for.
 */
export function bypassUnsavedChangesGuardOnce(): void {
  bypassOnce = true;
}

/**
 * Blocks in-app navigation away from a page with unsaved edits until the
 * user explicitly discards them. Hard refresh / tab close is covered
 * separately by each page's beforeunload listener.
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (component) => {
  if (bypassOnce) {
    bypassOnce = false;
    return true;
  }
  if (!component.hasUnsavedChanges()) return true;
  return inject(DialogService).confirm({
    variant: 'danger',
    title: 'Discard unsaved changes?',
    message: 'You have unsaved changes that will be lost if you leave this page.',
    confirmLabel: 'Discard changes',
    cancelLabel: 'Keep editing',
  });
};
